export class SageApiError extends Error {
  status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = "SageApiError"
    this.status = status
  }
}

type AxiosLike = {
  response?: {
    status?: number
    data?: unknown
  }
}

function isAxiosLike(error: unknown): error is AxiosLike {
  return Boolean(error && typeof error === "object" && "response" in error)
}

export function axiosStatus(error: unknown): number | undefined {
  if (isAxiosLike(error)) return error.response?.status
  return undefined
}

function looksLikeHtml(value: string): boolean {
  const trimmed = value.trim().toLowerCase()
  return trimmed.startsWith("<!doctype") || trimmed.startsWith("<html") || trimmed.includes("<body")
}

function messageFromData(data: unknown): string {
  if (!data) return ""
  if (typeof data === "string") return looksLikeHtml(data) ? "" : data
  if (typeof data === "object") {
    const body = data as { message?: unknown, error?: unknown }
    if (body.message != null && String(body.message).trim()) {
      const message = String(body.message)
      return looksLikeHtml(message) ? "" : message
    }
    if (body.error != null && String(body.error).trim()) {
      const error = String(body.error)
      return looksLikeHtml(error) ? "" : error
    }
  }
  return ""
}

export function axiosMessage(error: unknown): string {
  if (error instanceof SageApiError) return error.message
  if (isAxiosLike(error)) return messageFromData(error.response?.data)
  if (error instanceof Error) return error.message
  return ""
}

export function throwSageApiError(error: unknown, fallback: string): never {
  const status = axiosStatus(error) ?? 500
  const message = axiosMessage(error).trim() || fallback
  throw new SageApiError(status, message)
}

export async function messageFromBlobError(error: unknown): Promise<string> {
  if (error instanceof SageApiError) return error.message
  if (isAxiosLike(error)) {
    const data = error.response?.data
    if (typeof Blob !== "undefined" && data instanceof Blob) {
      try {
        const text = await data.text()
        if (!text) return ""
        try {
          return messageFromData(JSON.parse(text))
        } catch {
          return text
        }
      } catch {
        return ""
      }
    }
    return messageFromData(data)
  }
  return axiosMessage(error)
}
